import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { HistoryService } from '../../../../history/Service';
import { Check, CheckCheck, Circle, Eye, Pencil, Plus, Trash2, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const historyService = new HistoryService();

const actionIcons: Record<string, LucideIcon> = {
    CREATE: Plus,
    UPDATE: Pencil,
    CONFIRM: Check,
    APPROVE: Check,
    CONCLUDE: CheckCheck,
    CANCEL: X,
    DELETE: Trash2,
    VIEW: Eye,
};

const ChangeIcon = ({ action }: { action: string }) => {
    const Icon = actionIcons[action] || Circle;
    return <Icon aria-hidden="true" />;
};

// Registro de quem fez o que neste agendamento, do mais recente para o mais antigo (ordem da API).
export function ScheduleChanges({ scheduleId, active }: { scheduleId: string; active: boolean }) {
    const [items, setItems] = useState<any[]>([]);
    const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');
    const [retry, setRetry] = useState(0);
    const loadedKey = useRef('');

    useEffect(() => {
        const key = `${scheduleId}:${retry}`;
        if (!active || loadedKey.current === key) return;
        loadedKey.current = key;
        let current = true;
        setStatus('loading');
        historyService
            .find({ resourceId: scheduleId })
            .then(({ data }) => {
                if (!current) return;
                setItems((data || []).map(historyService.parseHistory));
                setStatus('ready');
            })
            .catch((error) => {
                console.error(error);
                if (current) setStatus('error');
            });
        return () => {
            current = false;
        };
    }, [active, scheduleId, retry]);

    if (status === 'loading') return <p className="sf-detail-empty" role="status">Carregando alterações...</p>;
    if (status === 'error') {
        return (
            <div className="sf-detail-empty" role="alert">
                <p>Não foi possível carregar as alterações.</p>
                <button type="button" className="sf-detail-link mt-1" onClick={() => setRetry((value) => value + 1)}>Tentar novamente</button>
            </div>
        );
    }
    if (!items.length) return <p className="sf-detail-empty">Nenhuma alteração registrada.</p>;

    return (
        <ul className="sf-detail-changes">
            {items.map((item) => (
                <li key={item._id}>
                    <span className="sf-detail-change-icon" aria-hidden="true">
                        <ChangeIcon action={item.action} />
                    </span>
                    <div className="min-w-0">
                        <p className="sf-detail-change-title">{item.message}</p>
                        <p className="sf-detail-past-meta">
                            {[item.user?.name, moment(item.createdAt).format('DD/MM/YYYY [às] HH:mm')].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                </li>
            ))}
        </ul>
    );
}
